// =====================================================================
// Faz X (2026-05-06) — Owner Mobile Cockpit
// =====================================================================
// "Cebimden bakacağım tek sayfa" — owner mobile için optimize.
// Telefonda bookmark edilip "Add to Home Screen" yapılırsa neredeyse
// app gibi açılır.
//
// Tek scroll, büyük dokunma alanları (44px+ touch targets), tüm KPI'lar
// hiyerarşik öncelik sırasında.
//
// Data kaynağı — paralel 4 endpoint çağrısı (Promise.all):
//   1. /api/admin/insights/forecast      → bugün ciro + dün karşılaştırma
//   2. /api/analytics/traffic            → şu an online
//   3. /api/admin/insights/notifications → kritik alert sayısı
//   4. /api/orders/all                   → son 5 sipariş
//
// Auto-refresh: 60 sn (mobile'da pil ömrü için 30sn değil 60sn).
// =====================================================================
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert, IconButton,
  Tooltip, LinearProgress, Avatar, List, ListItem, ListItemButton,
  ListItemText, Divider, Button
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import WarningIcon from "@mui/icons-material/Warning";
import StarIcon from "@mui/icons-material/Star";
import PublicIcon from "@mui/icons-material/Public";
import API from "../services/api";

// ──────────────────────────────────────────────────────────────────────
// Mobile için kompakt formatter'lar
// ──────────────────────────────────────────────────────────────────────

// Para — mobile'da kısaltma (bin / milyon)
const fmtMoneyShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M ₺`;
  if (n >= 1000)    return `${(n / 1000).toFixed(1)}K ₺`;
  return `${n.toFixed(0)} ₺`;
};

const fmtMoneyFull = (v) =>
  Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";

const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");

// "az önce / X dk önce / dün 14:30"
const timeAgo = (date) => {
  const d = new Date(date);
  const sec = Math.floor((Date.now() - d) / 1000);
  if (sec < 60) return "az önce";
  if (sec < 3600) return `${Math.floor(sec / 60)}dk`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}sa`;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
};

// Sipariş status → renk + Türkçe etiket
const statusMeta = (s) => {
  const map = {
    pending:         { color: "#94a3b8", label: "Bekliyor" },
    pending_payment: { color: "#f59e0b", label: "Ödeme Bekliyor" },
    confirmed:       { color: "#3b82f6", label: "Onaylandı" },
    preparing:       { color: "#8b5cf6", label: "Hazırlanıyor" },
    shipped:         { color: "#06b6d4", label: "Kargoda" },
    delivered:       { color: "#10b981", label: "Teslim Edildi" },
    cancelled:       { color: "#ef4444", label: "İptal" }
  };
  return map[s] || { color: "#64748b", label: s };
};

// ──────────────────────────────────────────────────────────────────────
// Ana bileşen
// ──────────────────────────────────────────────────────────────────────
export default function Cockpit() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    forecast: null,
    traffic: null,
    notifications: [],
    orders: [],
    goals: null  // Faz AG: aylık hedef widget için
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // ──────────────────────────────────────────────────────────────────
  // Tüm endpoint'leri paralel çek — Promise.all ile tek tick
  // Bir endpoint hata verirse diğerleri yine doldurulur (Promise.allSettled)
  // ──────────────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      // Faz AG: 5. endpoint olarak goals eklendi
      const [forecastR, trafficR, notifR, ordersR, goalsR] = await Promise.allSettled([
        API.get("/api/admin/insights/forecast"),
        API.get("/api/analytics/traffic"),
        API.get("/api/admin/insights/notifications"),
        API.get("/api/orders/all"),
        API.get("/api/admin/insights/goals")
      ]);

      setData({
        forecast: forecastR.status === "fulfilled" ? forecastR.value.data : null,
        traffic:  trafficR.status === "fulfilled"  ? trafficR.value.data  : null,
        notifications: notifR.status === "fulfilled" ? (notifR.value.data.items || []) : [],
        orders: ordersR.status === "fulfilled" ? (ordersR.value.data?.slice(0, 5) || []) : [],
        goals: goalsR.status === "fulfilled" ? goalsR.value.data : null
      });
      setErr(null);
      setLastRefresh(new Date());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  // İlk yükleme + 60 sn auto-refresh
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Forecast'ten bugün ciro + dün karşılaştırma çıkar
  // Forecast endpoint MTD veriyor, "bugün" için daily_trend'in son satırı
  const todayCell = data.forecast?.daily_trend?.[data.forecast.daily_trend.length - 1];
  const yesterdayCell = data.forecast?.daily_trend?.[data.forecast.daily_trend.length - 2];
  const todayRev = Number(todayCell?.revenue) || 0;
  const yesterdayRev = Number(yesterdayCell?.revenue) || 0;
  const dayChange = yesterdayRev > 0 ? Math.round(((todayRev - yesterdayRev) / yesterdayRev) * 100) : null;

  // Critical alert sayısı (severity = error)
  const criticalCount = data.notifications.filter(n => n.severity === "error").length;
  const warningCount = data.notifications.filter(n => n.severity === "warning").length;

  // Stok riski — forecast'taki stock_risk'ten ilk 3
  const topStockRisk = (data.forecast?.stock_risk || []).slice(0, 3);

  if (loading && !lastRefresh) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{
      maxWidth: 480,           // mobile-first; desktop'ta ortalansın
      mx: "auto",
      p: { xs: 1, sm: 1.5 },
      pb: 8                    // alt boşluk (FAB veya safe area için)
    }}>
      {/* Üst başlık — kompakt */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Cockpit</Typography>
          <Typography variant="caption" color="text.secondary">
            {lastRefresh ? `Son yenileme: ${timeAgo(lastRefresh)}` : "Yükleniyor..."}
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load} sx={{ width: 44, height: 44 }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* Hero: BUGÜN CIRO — sayfanın ana KPI'ı, en büyük yazı */}
      <Card sx={{
        p: 2.5, borderRadius: 4, mb: 2,
        background: "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
        color: "white"
      }}>
        <Typography variant="caption" sx={{ color: "#bfdbfe", letterSpacing: 1.5, fontWeight: 700 }}>
          BUGÜN CİRO
        </Typography>
        <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.1, mt: 0.5 }}>
          {fmtMoneyFull(todayRev)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Typography variant="body2" sx={{ color: "#bfdbfe" }}>
            {todayCell ? `${todayCell.orders} sipariş` : "henüz sipariş yok"}
          </Typography>
          {dayChange != null && (
            <Chip
              size="small"
              label={`${dayChange >= 0 ? "▲ +" : "▼ "}%${Math.abs(dayChange)}`}
              sx={{
                bgcolor: dayChange >= 0 ? "#10b981" : "#ef4444",
                color: "white", fontWeight: 700, fontSize: 11, height: 22
              }}
            />
          )}
        </Box>
        {/* Mini bilgi: dün ne idi */}
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mt: 1 }}>
          Dün: {fmtMoneyFull(yesterdayRev)}
        </Typography>
      </Card>

      {/* Faz AG: Aylık hedef widget — varsa göster, yoksa "hedef koy" CTA */}
      {data.goals && (() => {
        const goal = data.goals.goal;
        const progress = data.goals.progress;
        // Hedef yoksa hafif CTA göster, varsa progress göster
        if (!goal || !Number(goal.revenue_target)) {
          return (
            <Card
              onClick={() => navigate("/monthly-goals")}
              sx={{ p: 2, mb: 2, borderRadius: 3, cursor: "pointer", bgcolor: "#fef3c7", border: "1px dashed #f59e0b" }}
            >
              <Typography fontSize={13} fontWeight={700} color="#92400e">
                🏆 Bu ay hedef koymadın → ayarla, ilerleme takip et
              </Typography>
            </Card>
          );
        }
        // Hedef var — progress widget
        const pct = progress.revenue_pct || 0;
        const expectedPct = Math.round((progress.days_passed / progress.days_in_month) * 100);
        const onTrack = pct >= expectedPct;
        const color = pct >= 100 ? "#10b981" : pct >= 75 ? "#3b82f6" : pct >= 50 ? "#f59e0b" : "#ef4444";
        return (
          <Card
            onClick={() => navigate("/monthly-goals")}
            sx={{
              p: 2, mb: 2, borderRadius: 3, cursor: "pointer",
              border: `2px solid ${color}`,
              background: `linear-gradient(135deg, ${color}11 0%, ${color}05 100%)`
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box>
                <Typography fontSize={11} color="text.secondary" fontWeight={700}>🏆 AY HEDEFİ</Typography>
                <Typography fontSize={20} fontWeight={800} sx={{ color, lineHeight: 1.1 }}>
                  %{pct} {onTrack ? "✅" : "⚠️"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.disabled" fontSize={10}>
                  {fmtMoneyShort(data.goals.actuals?.revenue)} / <strong>{fmtMoneyShort(goal.revenue_target)}</strong>
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontSize: 9 }}>
                  {progress.days_passed}/{progress.days_in_month}g · beklenen %{expectedPct}
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(pct, 100)}
              sx={{ height: 8, borderRadius: 4, bgcolor: "#f1f5f9",
                "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 }
              }}
            />
          </Card>
        );
      })()}

      {/* 2 satırlı KPI: Online + Action */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6}>
          <Card
            onClick={() => navigate("/site-traffic")}
            sx={{ p: 2, borderRadius: 3, cursor: "pointer", height: "100%" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <RadioButtonCheckedIcon sx={{
                color: "#10b981", fontSize: 12,
                animation: data.traffic?.online_now > 0 ? "pulse 1.5s ease-in-out infinite" : "none",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.4 }
                }
              }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>ONLINE</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} color="#10b981">
              {data.traffic?.online_now || 0}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              son 5 dk
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card
            onClick={() => navigate("/action-center")}
            sx={{
              p: 2, borderRadius: 3, cursor: "pointer", height: "100%",
              border: criticalCount > 0 ? "2px solid #ef4444" : "2px solid transparent"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <NotificationImportantIcon sx={{
                color: criticalCount > 0 ? "#ef4444" : "#94a3b8", fontSize: 14
              }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>AKSİYON</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800}
              sx={{ color: criticalCount > 0 ? "#ef4444" : warningCount > 0 ? "#f59e0b" : "#10b981" }}>
              {criticalCount + warningCount}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {criticalCount > 0 ? `${criticalCount} acil` : warningCount > 0 ? `${warningCount} dikkat` : "temiz ✓"}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Ay sonu ciro projeksiyon — küçük bilgi kartı */}
      {data.forecast?.revenue_forecast && (
        <Card sx={{ p: 2, borderRadius: 3, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>AY SONU TAHMİNİ</Typography>
            <Typography variant="caption" color="text.disabled" fontSize={10}>
              {data.forecast.revenue_forecast.days_passed}/{data.forecast.revenue_forecast.days_in_month} gün
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} color="#1565C0">
            {fmtMoneyFull(data.forecast.revenue_forecast.projected_eom)}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Bu ay şu an: {fmtMoneyFull(data.forecast.revenue_forecast.mtd_revenue)} ·
            Geçen ay: {fmtMoneyFull(data.forecast.revenue_forecast.previous_month_full)}
          </Typography>
          {/* Ay ilerleme barı */}
          <LinearProgress
            variant="determinate"
            value={(data.forecast.revenue_forecast.days_passed / data.forecast.revenue_forecast.days_in_month) * 100}
            sx={{ height: 4, borderRadius: 2, mt: 1, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": { bgcolor: "#1565C0" }
            }}
          />
        </Card>
      )}

      {/* Kritik stok — ilk 3 ürün */}
      {topStockRisk.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 2, overflow: "hidden", border: "1px solid #fee2e2" }}>
          <Box sx={{ p: 1.5, bgcolor: "#fef2f2", display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon sx={{ color: "#ef4444", fontSize: 18 }} />
            <Typography fontWeight={700} fontSize={13} sx={{ flex: 1 }}>Stok Kritik</Typography>
            <Button size="small" onClick={() => navigate("/forecast")} sx={{ fontSize: 11 }}>Hepsi</Button>
          </Box>
          <List dense disablePadding>
            {topStockRisk.map((p, i) => (
              <React.Fragment key={p.id}>
                <ListItem sx={{ py: 0.8, px: 1.5 }}>
                  <ListItemText
                    primary={
                      <Typography fontSize={13} fontWeight={600} noWrap>
                        {p.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.disabled">
                        Stok: {Math.round(p.stock)} · Hız: {Number(p.daily_velocity).toFixed(1)}/gün
                      </Typography>
                    }
                  />
                  <Chip
                    label={`${Math.round(p.days_until_stockout || 0)}g`}
                    size="small"
                    sx={{
                      bgcolor: p.days_until_stockout <= 3 ? "#ef4444" :
                              p.days_until_stockout <= 7 ? "#f97316" : "#f59e0b",
                      color: "white", fontWeight: 700, fontSize: 11, minWidth: 40
                    }}
                  />
                </ListItem>
                {i < topStockRisk.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* Son 5 sipariş */}
      {data.orders.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 2, overflow: "hidden" }}>
          <Box sx={{ p: 1.5, bgcolor: "#f8fafc", display: "flex", alignItems: "center", gap: 1 }}>
            <ShoppingCartIcon sx={{ color: "#1565C0", fontSize: 18 }} />
            <Typography fontWeight={700} fontSize={13} sx={{ flex: 1 }}>Son Siparişler</Typography>
            <Button size="small" onClick={() => navigate("/orders")} sx={{ fontSize: 11 }}>Hepsi</Button>
          </Box>
          <List dense disablePadding>
            {data.orders.map((o, i) => {
              const sm = statusMeta(o.status);
              return (
                <React.Fragment key={o.id}>
                  <ListItemButton onClick={() => navigate(`/orders?id=${o.id}`)} sx={{ py: 1, px: 1.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography fontSize={13} fontWeight={700}>#{o.id}</Typography>
                          <Typography fontSize={13} fontWeight={800} color="#1565C0">
                            {fmtMoneyShort(o.total_price)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                          <Chip
                            label={sm.label}
                            size="small"
                            sx={{
                              bgcolor: sm.color + "22",
                              color: sm.color,
                              fontWeight: 700, fontSize: 10, height: 18
                            }}
                          />
                          <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
                            {timeAgo(o.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  {i < data.orders.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Card>
      )}

      {/* Quick Action grid — 4 buton, 2x2 */}
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ pl: 1 }}>HIZLI ERİŞİM</Typography>
      <Grid container spacing={1.5} mt={0.5}>
        <Grid item xs={6}>
          <Card onClick={() => navigate("/dashboard-v2")} sx={{
            p: 1.5, borderRadius: 3, cursor: "pointer", textAlign: "center",
            "&:active": { transform: "scale(0.95)" }, transition: "transform 0.1s"
          }}>
            <Avatar sx={{ bgcolor: "#1565C0", width: 36, height: 36, mx: "auto", mb: 0.5 }}>📊</Avatar>
            <Typography fontSize={12} fontWeight={700}>Yönetim</Typography>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card onClick={() => navigate("/nps")} sx={{
            p: 1.5, borderRadius: 3, cursor: "pointer", textAlign: "center",
            "&:active": { transform: "scale(0.95)" }, transition: "transform 0.1s"
          }}>
            <Avatar sx={{ bgcolor: "#f59e0b", width: 36, height: 36, mx: "auto", mb: 0.5 }}>
              <StarIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography fontSize={12} fontWeight={700}>NPS</Typography>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card onClick={() => navigate("/site-traffic")} sx={{
            p: 1.5, borderRadius: 3, cursor: "pointer", textAlign: "center",
            "&:active": { transform: "scale(0.95)" }, transition: "transform 0.1s"
          }}>
            <Avatar sx={{ bgcolor: "#10b981", width: 36, height: 36, mx: "auto", mb: 0.5 }}>
              <PublicIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography fontSize={12} fontWeight={700}>Trafik</Typography>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card onClick={() => navigate("/forecast")} sx={{
            p: 1.5, borderRadius: 3, cursor: "pointer", textAlign: "center",
            "&:active": { transform: "scale(0.95)" }, transition: "transform 0.1s"
          }}>
            <Avatar sx={{ bgcolor: "#8b5cf6", width: 36, height: 36, mx: "auto", mb: 0.5 }}>🔮</Avatar>
            <Typography fontSize={12} fontWeight={700}>Forecast</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Alt info */}
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", textAlign: "center", mt: 3, fontSize: 10 }}>
        Mobile cockpit · 60 sn'de bir otomatik yenilenir
      </Typography>
    </Box>
  );
}
